import React from 'react';
import { Header } from '../../components/Header';
import { Footer } from '../../components/Footer';
import { LocaleProvider, useLocale } from '../../i18n/LocaleContext';
import { useSeo } from '../../seo/useSeo';
import { breadcrumbJsonLd } from '../../seo/breadcrumbs';
import '../../styles/landing.css';
import '../../styles/landing-neon.css';

const CONTACT = {
  name: 'Maksym Bocharov',
  address: 'Karpatské námestie 7770/10A, 831 06, Bratislava-Rača, Slovakia',
  email: 'gather.community.org@gmail.com',
  phone: '+421 944 699 472',
};

const EFFECTIVE_DATE = 'May 5, 2026';

const TOC = [
  { id: 'acceptance', label: 'Acceptance of Terms' },
  { id: 'eligibility', label: 'Who can use Gather' },
  { id: 'account', label: 'Your account' },
  { id: 'use', label: 'Acceptable use' },
  { id: 'content', label: 'Your content' },
  { id: 'events', label: 'Events & organizers' },
  { id: 'ai', label: 'AI features' },
  { id: 'fees', label: 'Fees & payments' },
  { id: 'termination', label: 'Termination' },
  { id: 'disclaimer', label: 'Disclaimers' },
  { id: 'liability', label: 'Liability' },
  { id: 'indemnity', label: 'Indemnity' },
  { id: 'changes', label: 'Changes to these Terms' },
  { id: 'law', label: 'Governing law' },
  { id: 'contact', label: 'Contact' },
];

const PillarIcon = ({ children }: { children: React.ReactNode }): React.JSX.Element => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    {children}
  </svg>
);

const PILLARS: { icon: React.ReactNode; title: string; body: string }[] = [
  {
    icon: (
      <PillarIcon>
        <path d="M12 2l8 4v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6z" />
        <path d="M9 12l2 2 4-4" />
      </PillarIcon>
    ),
    title: 'Your content stays yours',
    body: 'You own what you post. We just need a license to show it.',
  },
  {
    icon: (
      <PillarIcon>
        <circle cx="12" cy="12" r="9" />
        <path d="M8 14s1.5 2 4 2 4-2 4-2M9 9h.01M15 9h.01" />
      </PillarIcon>
    ),
    title: 'Be kind, be real',
    body: 'Real people, real events. No spam, hate, or scams.',
  },
  {
    icon: (
      <PillarIcon>
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
      </PillarIcon>
    ),
    title: 'Organizer-led events',
    body: 'Events are run by hosts, not by us. Read the details before you go.',
  },
  {
    icon: (
      <PillarIcon>
        <rect x="3" y="3" width="18" height="18" rx="3" />
        <path d="M9 9h6M9 13h6M9 17h4" />
      </PillarIcon>
    ),
    title: 'Slovak law',
    body: 'These Terms are governed by Slovak law. Operator based in Bratislava.',
  },
];

const TermsInner = (): React.JSX.Element => {
  const { t } = useLocale();

  useSeo({
    title: 'Terms of Service',
    description:
      'The agreement between you and Gather: how the app works, acceptable use, host responsibilities, payments, and how disputes are handled.',
    canonical: '/terms',
    jsonLd: breadcrumbJsonLd([
      { name: 'Home', path: '/' },
      { name: 'Terms', path: '/terms' },
    ]),
  });

  return (
    <div className="landing landing--neon" id="top">
      <Header />
      <main>
        <section className="privacy-page">
          <div className="wrap privacy-wrap">
            <aside className="privacy-toc">
              <div className="privacy-toc-inner">
                <span className="privacy-toc-label">On this page</span>
                <ol>
                  {TOC.map((item, i) => (
                    <li key={item.id}>
                      <a href={`#${item.id}`}>
                        <span className="toc-num">{String(i + 1).padStart(2, '0')}</span>
                        <span className="toc-text">{item.label}</span>
                      </a>
                    </li>
                  ))}
                </ol>
              </div>
            </aside>

            <article className="privacy-content">
              <header className="privacy-hero">
                <span className="eyebrow">{t('terms.eyebrow')}</span>
                <h1>Terms of Service</h1>
                <div className="privacy-meta-row">
                  <span className="privacy-tag">Effective {EFFECTIVE_DATE}</span>
                  <span className="privacy-tag">~ 5 min read</span>
                  <span className="privacy-tag">EN</span>
                </div>
                <p className="privacy-intro">
                  These Terms are the agreement between you and Gather. They explain what you can
                  expect from us, and what we expect from you when you use the app or website.
                  Please read them — short, no legalese where we can avoid it.
                </p>
              </header>

              <div className="privacy-pillars">
                {PILLARS.map((p) => (
                  <div className="pillar" key={p.title}>
                    <div className="pillar-icon">{p.icon}</div>
                    <h3>{p.title}</h3>
                    <p>{p.body}</p>
                  </div>
                ))}
              </div>

              <div className="legal-card">
                <h2 style={{ fontSize: 22, marginBottom: 8, marginTop: 0 }}>Operator</h2>
                <p style={{ color: '#4a4255', marginBottom: 16 }}>
                  Gather is operated as a sole proprietorship by:
                </p>
                <dl className="legal-dl">
                  <dt>Name</dt>
                  <dd>{CONTACT.name}</dd>
                  <dt>Address</dt>
                  <dd>{CONTACT.address}</dd>
                  <dt>Email</dt>
                  <dd><a href={`mailto:${CONTACT.email}`}>{CONTACT.email}</a></dd>
                  <dt>Phone</dt>
                  <dd><a href={`tel:${CONTACT.phone.replace(/\s+/g, '')}`}>{CONTACT.phone}</a></dd>
                </dl>
              </div>

              <Section id="acceptance" num={1} title="Acceptance of these Terms">
                <p>
                  By creating an account, downloading, or using Gather, you agree to these Terms
                  and to our <a href="/privacy">Privacy Policy</a>. If you do not agree, please do
                  not use the Service.
                </p>
              </Section>

              <Section id="eligibility" num={2} title="Who can use Gather">
                <ul>
                  <li>You must be at least 16 years old.</li>
                  <li>You must provide accurate information when you sign up.</li>
                  <li>You must not be barred from using the Service under applicable law or by a previous suspension.</li>
                </ul>
              </Section>

              <Section id="account" num={3} title="Your account">
                <p>
                  You are responsible for keeping your account credentials secure. Don't share your
                  account, and let us know right away if you think someone else has access.
                </p>
                <p>
                  We may need to verify your identity (for example, via email code) before
                  performing sensitive actions.
                </p>
              </Section>

              <Section id="use" num={4} title="Acceptable use">
                <p>To keep Gather safe and useful for everyone, you agree <strong>not</strong> to:</p>
                <ul>
                  <li>Post content that is illegal, hateful, harassing, threatening, sexually explicit, or that promotes self-harm</li>
                  <li>Impersonate another person or misrepresent who you are</li>
                  <li>Spam, scrape, or scam other users</li>
                  <li>Run paid ads or promotions inside events without our permission</li>
                  <li>Use Gather to organize illegal activity or events that violate local law</li>
                  <li>Try to bypass security, scrape data, or interfere with the Service</li>
                  <li>Use bots or automated tools to interact with the Service</li>
                  <li>Reverse-engineer the app or attempt to extract our source code</li>
                </ul>
                <p>
                  We may remove content that breaks these rules and suspend or terminate accounts
                  that repeat or seriously violate them.
                </p>
              </Section>

              <Section id="content" num={5} title="Your content">
                <p>
                  You keep all rights to the content you post on Gather (events, photos, posts,
                  messages, etc.). You don't transfer ownership to us.
                </p>
                <p>
                  By posting content, you grant us a worldwide, non-exclusive, royalty-free license
                  to host, store, display, and distribute that content for the purpose of operating
                  and improving the Service. This license ends when you remove the content or
                  delete your account, except that:
                </p>
                <ul>
                  <li>Reasonable backups may persist for up to 90 days, and</li>
                  <li>Content you sent to other users (e.g. chat messages) may remain visible to those users until they delete it.</li>
                </ul>
                <p>
                  You confirm that you have the right to post the content you upload and that it
                  doesn't infringe anyone else's rights.
                </p>
              </Section>

              <Section id="events" num={6} title="Events & organizers">
                <p>
                  Events on Gather are organized by users, not by us. We do not host the events,
                  vet venues, supervise activities, or guarantee that an event will happen as
                  described.
                </p>
                <ul>
                  <li><strong>If you host an event:</strong> you are responsible for accurate descriptions, the venue, safety, applicable local laws, and any tickets, fees, or refunds.</li>
                  <li><strong>If you join an event:</strong> use your judgment. Read the description, meet in safe places, and follow the organizer's rules.</li>
                </ul>
                <p>
                  We are not a party to the agreement between an organizer and an attendee, and we
                  are not liable for what happens at an event.
                </p>
              </Section>

              <Section id="ai" num={7} title="AI features">
                <p>
                  Some features (suggestions, summaries, auto-fill) are powered by an AI model
                  provided by OpenAI. AI output can be inaccurate, biased, or out of context — use
                  your judgment before relying on it. You are responsible for any content you
                  publish, even if it was generated with AI assistance.
                </p>
              </Section>

              <Section id="fees" num={8} title="Fees & payments">
                <p>
                  Using Gather is free. We do not currently process payments inside the app.
                </p>
                <p>
                  Where an event is paid, payment is arranged directly between the attendee and the
                  organizer (for example, in cash or via the organizer's preferred method). We are
                  not a party to that transaction and are not responsible for refunds or disputes
                  between you and an organizer.
                </p>
              </Section>

              <Section id="termination" num={9} title="Termination">
                <p>
                  You can stop using Gather at any time and delete your account from inside the app
                  (Profile → Settings → Delete account) or by emailing us. Account deletion is
                  immediate; encrypted backups are overwritten within 90 days.
                </p>
                <p>
                  We may suspend or terminate your access if you break these Terms, if continued
                  use would create legal or safety risk, or if we are required to do so by law.
                  Where reasonable, we'll let you know.
                </p>
              </Section>

              <Section id="disclaimer" num={10} title="Disclaimers">
                <p>
                  Gather is provided <strong>"as is"</strong> and <strong>"as available"</strong>,
                  without warranties of any kind, express or implied, to the maximum extent
                  permitted by law. We do not warrant that the Service will be uninterrupted,
                  error-free, or that content posted by users is accurate or trustworthy.
                </p>
              </Section>

              <Section id="liability" num={11} title="Limitation of liability">
                <p>
                  To the maximum extent permitted by law, neither we nor any of our partners or
                  service providers are liable for any indirect, incidental, special, consequential,
                  or punitive damages, or for lost profits, lost data, or business interruption,
                  arising out of or related to your use of the Service.
                </p>
                <p>
                  Where we are liable for direct damages, our total liability is limited to the
                  greater of (a) the fees you paid us in the 12 months before the claim (typically
                  zero, since the app is free) or (b) one hundred euros (€100). Nothing in these
                  Terms limits liability that cannot be limited under applicable law (such as
                  liability for gross negligence, intentional misconduct, or harm to consumer
                  rights protected by mandatory law).
                </p>
              </Section>

              <Section id="indemnity" num={12} title="Indemnity">
                <p>
                  You agree to indemnify and hold us harmless from claims, damages, and expenses
                  (including reasonable legal fees) arising from your content, your use of the
                  Service, or your breach of these Terms — except to the extent caused by us.
                </p>
              </Section>

              <Section id="changes" num={13} title="Changes to these Terms">
                <p>
                  We may update these Terms from time to time. When we make material changes, we
                  will update the "Effective date" above and, where appropriate, notify you in the
                  app or by email. Continued use of the Service after the update means you accept
                  the revised Terms.
                </p>
              </Section>

              <Section id="law" num={14} title="Governing law & disputes">
                <p>
                  These Terms are governed by the laws of the Slovak Republic, without regard to
                  conflict-of-laws rules. Any dispute arising out of or in connection with these
                  Terms or the Service will be resolved by the competent courts of Bratislava,
                  Slovakia, unless mandatory consumer-protection law in your country gives you the
                  right to bring a claim before a local court.
                </p>
                <p>
                  If you are a consumer in the EU, you can also use the European Commission's{' '}
                  <a href="https://ec.europa.eu/consumers/odr/" target="_blank" rel="noopener noreferrer">
                    Online Dispute Resolution platform
                  </a>{' '}
                  (we are not obligated to participate in alternative dispute resolution but may do
                  so voluntarily).
                </p>
              </Section>

              <Section id="contact" num={15} title="Contact">
                <p>
                  Questions about these Terms? Email{' '}
                  <a href={`mailto:${CONTACT.email}`} style={{ color: 'var(--purple-600)', fontWeight: 600 }}>
                    {CONTACT.email}
                  </a>{' '}
                  or write to us at the address in the Operator card above.
                </p>
              </Section>

              <p className="legal-operator">Operator: {CONTACT.name}</p>
            </article>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

type SectionProps = {
  id: string;
  num: number;
  title: string;
  children: React.ReactNode;
};

const Section = ({ id, num, title, children }: SectionProps): React.JSX.Element => (
  <section id={id} className="privacy-section">
    <h2>
      <span className="privacy-section-num">{String(num).padStart(2, '0')}</span>
      <span>{title}</span>
    </h2>
    {children}
  </section>
);

export const Terms = (): React.JSX.Element => (
  <LocaleProvider>
    <TermsInner />
  </LocaleProvider>
);
